import { Button, Modal, modalWidth, ToolTip } from "@hubrise/react-components"
import * as React from "react"
import { useTranslation } from "react-i18next"

import useOptionListUsages from "Catalog/Edit/shared/hooks/useOptionListUsages"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import Table, { Footer } from "shared/components/Table"
import useEditor from "shared/hooks/useEditor"
import useNavigateNoUpdate from "shared/hooks/useNavigateNoUpdate"
import { IOptionList, IProduct, ISku } from "shared/hubrise/models"
import * as OptionList from "shared/hubrise/utils/OptionList"

import AttachOptionList from "../AttachOptionList"

import { Empty, Name, MinMax, Options, Link } from "./Styles"

interface SkuOptionListsProps {
  product: IProduct
  sku: ISku
}

const SkuOptionLists = ({ product, sku }: SkuOptionListsProps): JSX.Element => {
  const { t } = useTranslation()
  const { editor, catalog } = useEditor()
  const { catalogNavigation } = useCatalogNavigation()
  const navigate = useNavigateNoUpdate()
  const optionListUsages = useOptionListUsages("long")

  const optionLists: Array<IOptionList> = sku.optionListIds.map((id) => OptionList.byId(catalog, id))

  if (optionLists.length === 0) {
    return (
      <Empty>
        {t("product.option_lists.empty") + " "}
        <Modal
          modalWidth={modalWidth.medium}
          renderLink={({ open }) => <Link onClick={open}>{t("product.option_lists.attach")}</Link>}
          renderContent={({ close }) => <AttachOptionList onClose={close} {...{ product, sku }} />}
        />
      </Empty>
    )
  }

  return (
    <>
      <Table
        items={optionLists}
        columns={[
          {
            header: t("product.option_lists.name"),
            cellRenderer: (optionList) => (
              <Name onClick={() => navigate(catalogNavigation.editPath("option_list", optionList.id))}>
                <ToolTip label={optionListUsages(optionList)}>{optionList.name}</ToolTip>
              </Name>
            ),
          },
          {
            header: t("product.option_lists.min_max"),
            cellRenderer: (optionList) => (
              <MinMax>
                {optionList.min_selections} /{" "}
                {optionList.max_selections === null ? t("product.option_lists.unlimited") : optionList.max_selections}
              </MinMax>
            ),
          },
          {
            header: t("product.option_lists.options"),
            cellRenderer: (optionList) => <Options>{optionList.options.map(({ name }) => name).join(", ")}</Options>,
          },
        ]}
        onReorder={(newOptionLists) => {
          editor.sku.reorderOptionLists(
            product.id,
            sku.id,
            newOptionLists.map((optionList) => optionList.id),
          )
        }}
        onDelete={(optionList) => editor.sku.detachOptionList(product.id, sku.id, optionList.id)}
        droppableId={`optionLists_${sku.id}`}
      />

      <Footer>
        <Modal
          modalWidth={modalWidth.medium}
          renderLink={({ open }) => (
            <Button icon="add" variant="empty" onClick={open}>
              {t("product.option_lists.attach")}
            </Button>
          )}
          renderContent={({ close }) => <AttachOptionList onClose={close} {...{ product, sku }} />}
        />
      </Footer>
    </>
  )
}

export default SkuOptionLists
